import type { AxiosError } from 'axios';
import { useRouter } from 'expo-router';
import { ArrowLeft, Sparkles } from 'lucide-react-native';
import { useCallback, useRef, useState } from 'react';
import {
  ActivityIndicator,
  type LayoutChangeEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  type TextInput,
  type TextInputProps,
  useWindowDimensions,
  View,
} from 'react-native';
import { FadeIn } from '../../../../src/components/animations';
import { Box, HStack, Text, VStack } from '../../../../src/components/primitives';
import { Button } from '../../../../src/components/primitives/Button';
import { FormInput } from '../../../../src/components/primitives/FormInput';
import { PhoneNumberInput } from '../../../../src/components/primitives/PhoneNumberInput';
import { Select } from '../../../../src/components/primitives/Select';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useHideMobileTabBar } from '../../../../src/contexts/LayoutChromeContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import { useTranslation } from '../../../../src/contexts/TranslationContext';
import { AssigneePicker } from '../../../../src/features/leads/components/AssigneePicker';
import { useCreateLead, useFunnelTemplates } from '../../../../src/hooks/useLeads';
import { useUnsavedChangesGuard } from '../../../../src/hooks/useUnsavedChangesGuard';
import { colors } from '../../../../src/theme/colors';
import { tokens } from '../../../../src/theme/tokens';
import {
  CONTACT_ROLE_OPTIONS,
  type ContactRole,
  type LeadSource,
} from '../../../../src/types/leads';
import { isValidLeadPhone } from '../../../../src/utils/phone';

interface ValidationErrorResponse {
  message: string | string[];
  statusCode: number;
}

function useSourceOptions(): Array<{ value: LeadSource; label: string }> {
  const { t } = useTranslation();
  return [
    { value: 'manual', label: t('common.sources.manual') },
    { value: 'website', label: t('common.sources.website') },
    { value: 'referral', label: t('common.sources.referral') },
    { value: 'email', label: t('common.sources.email') },
    { value: 'linkedin', label: t('common.sources.linkedin') },
    { value: 'web_form', label: t('common.sources.form') },
    { value: 'other', label: t('common.sources.other') },
  ];
}

interface FormFieldProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  keyboardType?: TextInputProps['keyboardType'];
  required?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
  testID?: string;
  error?: string;
}

function FormField({
  label,
  value,
  onChangeText,
  placeholder,
  keyboardType = 'default',
  required,
  multiline = false,
  numberOfLines,
  testID,
  error,
}: Readonly<FormFieldProps>) {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<TextInput>(null);

  const inputShellStyle = [
    styles.inputShell,
    isHovered && styles.inputShellHovered,
    isFocused && styles.inputShellFocused,
    multiline && styles.inputShellMultiline,
    Boolean(error) && styles.inputShellError,
  ];

  return (
    <VStack spacing="xs">
      <Text variant="caption" color={colors.text.secondary} style={styles.fieldLabel}>
        {label}
        {required && (
          <Text variant="caption" color={colors.status.error.main}>
            {' '}
            *
          </Text>
        )}
      </Text>
      <Pressable
        accessible={false}
        style={inputShellStyle}
        onPress={() => inputRef.current?.focus()}
        onPointerEnter={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
        onPointerLeave={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
      >
        <FormInput
          ref={inputRef}
          testID={testID}
          accessibilityLabel={label}
          accessibilityValue={value ? { text: value } : undefined}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          keyboardType={keyboardType}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          multiline={multiline}
          numberOfLines={numberOfLines}
          autoCapitalize={
            keyboardType === 'email-address' || keyboardType === 'url' ? 'none' : 'words'
          }
          autoCorrect={false}
          style={[styles.input, multiline && styles.inputMultiline]}
        />
      </Pressable>
      {error ? (
        <Text
          variant="caption"
          color={colors.status.error.main}
          testID={testID ? `${testID}_error` : undefined}
        >
          {error}
        </Text>
      ) : null}
    </VStack>
  );
}

export default function NewLeadScreen() {
  useHideMobileTabBar();
  const router = useRouter();
  const { isAuthenticated, isLoading: authLoading, currentOrganization } = useAuth();
  const { t } = useTranslation();
  const { showSuccess } = useToast();
  const sourceOptions = useSourceOptions();
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;

  const orgId = currentOrganization?.id;
  const { data: templates } = useFunnelTemplates(orgId);
  const activeTemplate = templates?.find((template) => template.isActive);
  const createLead = useCreateLead();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [companyWebsiteUrl, setCompanyWebsiteUrl] = useState('');
  const [companyLinkedinUrl, setCompanyLinkedinUrl] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [source, setSource] = useState<LeadSource>('manual');
  const [primaryContactRole, setPrimaryContactRole] = useState<ContactRole | ''>('');
  const [tags, setTags] = useState('');
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);

  const scrollViewRef = useRef<ScrollView>(null);
  const fieldPositions = useRef<Record<string, number>>({});

  const registerFieldPosition = (field: string) => (event: LayoutChangeEvent) => {
    fieldPositions.current[field] = event.nativeEvent.layout.y;
  };

  const clearFieldErrors = useCallback((fields: string[]) => {
    setFieldErrors((prev) => {
      if (!fields.some((field) => field in prev)) return prev;
      const next = { ...prev };
      for (const field of fields) delete next[field];
      return next;
    });
  }, []);

  const handleFullNameChange = useCallback(
    (text: string) => {
      setFullName(text);
      clearFieldErrors(['fullName', 'company']);
    },
    [clearFieldErrors],
  );

  const handleCompanyChange = useCallback(
    (text: string) => {
      setCompany(text);
      clearFieldErrors(['fullName', 'company']);
    },
    [clearFieldErrors],
  );

  const handleEmailChange = useCallback(
    (text: string) => {
      setEmail(text);
      clearFieldErrors(['email', 'phone']);
    },
    [clearFieldErrors],
  );

  const handlePhoneChange = useCallback(
    (text: string) => {
      setPhone(text);
      clearFieldErrors(['email', 'phone']);
    },
    [clearFieldErrors],
  );

  useUnsavedChangesGuard(
    !savedSuccessfully &&
      Boolean(
        fullName ||
          email ||
          phone ||
          company ||
          companyWebsiteUrl ||
          companyLinkedinUrl ||
          jobTitle ||
          primaryContactRole ||
          tags ||
          assignedTo,
      ),
  );

  const handleCreate = useCallback(async () => {
    if (!orgId || !activeTemplate) return;
    setFormError('');

    const errors: Record<string, string> = {};
    if (!company.trim() && !fullName.trim()) {
      errors.fullName = t('leads.new.companyOrContactRequired');
      errors.company = t('leads.new.companyOrContactRequired');
    }
    if (!email.trim() && !phone.trim()) {
      errors.email = t('leads.new.emailOrPhoneRequired');
      errors.phone = t('leads.new.emailOrPhoneRequired');
    }
    if (phone.trim() && !isValidLeadPhone(phone)) {
      errors.phone = t('leads.new.phoneInvalid');
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const positions = Object.keys(errors)
        .map((field) => fieldPositions.current[field])
        .filter((y): y is number => typeof y === 'number');
      if (positions.length > 0) {
        scrollViewRef.current?.scrollTo({
          y: Math.max(Math.min(...positions) - 24, 0),
          animated: true,
        });
      }
      return;
    }

    setFieldErrors({});

    const companyProfile: Record<string, string> = {};
    if (companyWebsiteUrl.trim()) companyProfile.companyUrl = companyWebsiteUrl.trim();
    if (companyLinkedinUrl.trim()) companyProfile.companyLinkedinUrl = companyLinkedinUrl.trim();

    try {
      await createLead.mutateAsync({
        organizationId: orgId,
        funnelTemplateId: activeTemplate.id,
        fullName: fullName || undefined,
        email: email || undefined,
        phone: phone || undefined,
        company: company || undefined,
        jobTitle: jobTitle || undefined,
        primaryContactRole: primaryContactRole || undefined,
        source,
        customFields: Object.keys(companyProfile).length ? { companyProfile } : undefined,
        tags: tags
          ? tags
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean)
          : undefined,
        assignedTo: assignedTo ?? undefined,
      });

      setSavedSuccessfully(true);
      showSuccess(t('leads.new.successToast'));
      router.canGoBack() ? router.back() : router.replace('/leads');
    } catch (error) {
      const axiosError = error as AxiosError<ValidationErrorResponse>;
      const statusCode = axiosError.response?.status;
      if (statusCode === 409) {
        setFormError(t('leads.new.duplicateError'));
        return;
      }
      const responseData = axiosError.response?.data;
      if (responseData?.message) {
        const messages = Array.isArray(responseData.message)
          ? responseData.message
          : [responseData.message];
        setFormError(messages.join('. '));
      }
    }
  }, [
    activeTemplate,
    assignedTo,
    company,
    companyLinkedinUrl,
    companyWebsiteUrl,
    createLead,
    email,
    fullName,
    jobTitle,
    orgId,
    phone,
    primaryContactRole,
    router,
    showSuccess,
    source,
    t,
    tags,
  ]);

  if (authLoading) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center">
        <ActivityIndicator size="large" color={colors.primary[500]} />
      </Box>
    );
  }

  if (!isAuthenticated) return null;

  const canSubmit = Boolean(orgId && activeTemplate);

  return (
    <ScrollView
      ref={scrollViewRef}
      testID="new_lead_screen"
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <Box style={[styles.formContainer, isDesktop && styles.formContainerDesktop]}>
        <FadeIn delay={0} translateY={12}>
          <VStack spacing="lg">
            <HStack spacing="md" align="center">
              <Pressable
                testID="new_lead_back_button"
                onPress={() => (router.canGoBack() ? router.back() : router.replace('/leads'))}
                accessibilityRole="button"
              >
                <Box
                  width={38}
                  height={38}
                  borderRadius="md"
                  alignItems="center"
                  justifyContent="center"
                  style={styles.backButton}
                >
                  <ArrowLeft size={18} color={colors.text.secondary} strokeWidth={2} />
                </Box>
              </Pressable>
              <VStack spacing="xs">
                <HStack spacing="xs" align="center">
                  <Sparkles size={14} color={colors.primary.main} strokeWidth={2} />
                  <Text variant="caption" color={colors.primary.main} style={styles.newLeadBadge}>
                    {t('leads.new.title')}
                  </Text>
                </HStack>
                <Text variant="h4" color={colors.text.primary} style={styles.title}>
                  {t('leads.new.subtitle')}
                </Text>
              </VStack>
            </HStack>

            {!activeTemplate && (
              <Box backgroundColor={colors.status.warning.bg} borderRadius="md" padding="md">
                <Text variant="bodySmall" color={colors.status.warning.main}>
                  {t('leads.new.noFunnel')}
                </Text>
              </Box>
            )}

            <Box style={styles.formCard}>
              <VStack spacing="lg">
                <Text variant="h6" color={colors.text.primary} style={styles.sectionTitle}>
                  {t('leads.new.contactInfo')}
                </Text>

                <View onLayout={registerFieldPosition('fullName')}>
                  <FormField
                    label={t('leads.new.contactName')}
                    value={fullName}
                    onChangeText={handleFullNameChange}
                    placeholder={t('leads.new.contactNamePlaceholder')}
                    required
                    error={fieldErrors.fullName}
                    testID="new_lead_fullname_input"
                  />
                </View>
                <View onLayout={registerFieldPosition('email')}>
                  <FormField
                    label={t('leads.new.emailAddress')}
                    value={email}
                    onChangeText={handleEmailChange}
                    placeholder={t('leads.new.emailPlaceholder')}
                    keyboardType="email-address"
                    required
                    error={fieldErrors.email}
                    testID="new_lead_email_input"
                  />
                </View>
                <View onLayout={registerFieldPosition('phone')}>
                  <PhoneNumberInput
                    label={t('leads.new.phoneNumber')}
                    value={phone}
                    onChangeText={handlePhoneChange}
                    placeholder={t('leads.new.phonePlaceholder')}
                    required
                    error={fieldErrors.phone}
                    testID="new_lead_phone_input"
                    searchPlaceholder={t('leads.new.phoneCountrySearch')}
                    modalTitle={t('leads.new.phoneCountryTitle')}
                  />
                </View>
                <View onLayout={registerFieldPosition('company')}>
                  <FormField
                    label={t('leads.new.company')}
                    value={company}
                    onChangeText={handleCompanyChange}
                    placeholder={t('leads.new.companyPlaceholder')}
                    required
                    error={fieldErrors.company}
                    testID="new_lead_company_input"
                  />
                </View>
                <FormField
                  label={t('leads.new.companyWebsiteUrl')}
                  value={companyWebsiteUrl}
                  onChangeText={setCompanyWebsiteUrl}
                  placeholder={t('leads.new.companyWebsiteUrlPlaceholder')}
                  keyboardType="url"
                  testID="new_lead_company_website_input"
                />
                <FormField
                  label={t('leads.new.companyLinkedinUrl')}
                  value={companyLinkedinUrl}
                  onChangeText={setCompanyLinkedinUrl}
                  placeholder={t('leads.new.companyLinkedinUrlPlaceholder')}
                  keyboardType="url"
                  testID="new_lead_company_linkedin_input"
                />
                <FormField
                  label={t('leads.new.jobTitle')}
                  value={jobTitle}
                  onChangeText={setJobTitle}
                  placeholder={t('leads.new.jobTitlePlaceholder')}
                  testID="new_lead_jobtitle_input"
                />

                <VStack spacing="xs">
                  <Text variant="caption" color={colors.text.secondary} style={styles.fieldLabel}>
                    {t('leads.new.roleLabel')}
                  </Text>
                  <Select
                    testID="new_lead_role_select"
                    variant="dropdown"
                    value={primaryContactRole}
                    onChange={(v) => setPrimaryContactRole(v as ContactRole | '')}
                    placeholder={t('leads.new.rolePlaceholder')}
                    options={CONTACT_ROLE_OPTIONS.map((r) => ({
                      value: r,
                      label: t(`leads.contacts.roles.${r}`),
                    }))}
                  />
                </VStack>

                <VStack spacing="xs">
                  <Text variant="caption" color={colors.text.secondary} style={styles.fieldLabel}>
                    {t('leads.new.source')}
                  </Text>
                  <HStack spacing="sm" style={styles.sourceWrap}>
                    {sourceOptions.map((option) => {
                      const active = source === option.value;
                      return (
                        <Pressable
                          key={option.value}
                          testID={`new_lead_source_${option.value}_button`}
                          onPress={() => setSource(option.value)}
                        >
                          <Box
                            borderRadius="full"
                            borderWidth={1}
                            paddingX="md"
                            paddingY="xs"
                            style={[
                              styles.sourceChip,
                              active ? styles.sourceChipActive : styles.sourceChipInactive,
                            ]}
                          >
                            <Text
                              variant="caption"
                              color={active ? colors.primary[500] : colors.text.secondary}
                              style={active ? styles.sourceChipTextActive : styles.sourceChipText}
                            >
                              {option.label}
                            </Text>
                          </Box>
                        </Pressable>
                      );
                    })}
                  </HStack>
                </VStack>

                <FormField
                  label={t('leads.new.tags')}
                  value={tags}
                  onChangeText={setTags}
                  placeholder={t('leads.new.tagsPlaceholder')}
                  testID="new_lead_tags_input"
                />

                {orgId && (
                  <VStack spacing="xs" style={{ zIndex: 20, position: 'relative' }}>
                    <Text variant="caption" color={colors.text.secondary} style={styles.fieldLabel}>
                      {t('leads.detail.assignedTo')}
                    </Text>
                    <AssigneePicker
                      organizationId={orgId}
                      value={assignedTo}
                      onChange={setAssignedTo}
                    />
                  </VStack>
                )}

                {(createLead.isError || Boolean(formError)) && (
                  <Box backgroundColor={colors.status.error.bg} borderRadius="md" padding="md">
                    <Text variant="bodySmall" color={colors.status.error.main}>
                      {formError || t('leads.new.createError')}
                    </Text>
                  </Box>
                )}

                <HStack spacing="md" justify="end" style={styles.actionRow}>
                  <Button
                    testID="new_lead_cancel_button"
                    variant="ghost"
                    size="md"
                    onPress={() => (router.canGoBack() ? router.back() : router.replace('/leads'))}
                  >
                    {t('common.actions.cancel')}
                  </Button>
                  <Button
                    testID="new_lead_submit_button"
                    variant="primary"
                    size="md"
                    onPress={handleCreate}
                    loading={createLead.isPending}
                    disabled={!canSubmit}
                  >
                    {t('leads.new.createLead')}
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </FadeIn>
      </Box>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: Platform.OS === 'web' ? tokens.spacing.lg : tokens.spacing.xl,
    paddingTop: tokens.spacing['2xl'],
    paddingBottom: tokens.spacing['3xl'],
  },
  formContainer: {
    width: '100%',
    maxWidth: 680,
    alignSelf: 'center',
  },
  formContainerDesktop: {
    maxWidth: 760,
  },
  backButton: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.cardOverlay,
  },
  newLeadBadge: {
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    fontWeight: '700',
  },
  title: {
    fontWeight: '700',
    letterSpacing: -0.4,
  },
  formCard: {
    borderRadius: tokens.radius.xl,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.cardOverlay,
    padding: tokens.spacing.lg,
  },
  sectionTitle: {
    fontWeight: '600',
  },
  fieldLabel: {
    fontWeight: '500',
    letterSpacing: 0.3,
  },
  inputShell: {
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.input,
    backgroundColor: colors.background.input,
    paddingHorizontal: tokens.spacing.md,
    minHeight: 48,
    justifyContent: 'center',
  },
  inputShellHovered: {
    borderColor: colors.border.default,
    backgroundColor: colors.background.elevated,
  },
  inputShellFocused: {
    borderColor: colors.primary.main,
    backgroundColor: colors.background.elevated,
    transform: [{ translateY: -1 }],
  },
  inputShellMultiline: {
    minHeight: 92,
    justifyContent: 'flex-start',
    paddingTop: tokens.spacing.sm,
    paddingBottom: tokens.spacing.sm,
  },
  inputShellError: {
    borderColor: colors.status.error.main,
  },
  input: {
    width: '100%',
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    minHeight:
      Platform.OS === 'android'
        ? tokens.interaction.minimumTarget.android
        : tokens.interaction.minimumTarget.default,
  },
  inputMultiline: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  sourceWrap: {
    flexWrap: 'wrap',
    rowGap: tokens.spacing.xs,
  },
  sourceChip: {
    marginBottom: tokens.spacing.xs,
  },
  sourceChipActive: {
    backgroundColor: colors.primary.muted,
    borderColor: colors.primary.borderActive,
  },
  sourceChipInactive: {
    backgroundColor: colors.surface.cardOverlay,
    borderColor: colors.border.subtle,
  },
  sourceChipText: {
    fontWeight: '500',
  },
  sourceChipTextActive: {
    fontWeight: '700',
  },
  actionRow: {
    marginTop: tokens.spacing.sm,
  },
});
